import { useState, useEffect, useMemo } from "react";
import Markdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkMath from "remark-math";
import { motion } from "framer-motion";
import "katex/dist/katex.min.css";

interface TypewriterMarkdownProps {
    content: string;
    delay?: number;
    className?: string;
    onUpdate?: () => void;
    isLast?: boolean;
    onFinish?: () => void;
}

const TypewriterMarkdown = ({
    content,
    delay = 10,
    className,
    onUpdate,
    isLast,
    onFinish,
}: TypewriterMarkdownProps) => {
    const [displayedContent, setDisplayedContent] = useState("");
    const [charIndex, setCharIndex] = useState(0);

    const segments = useMemo(() => content.split(""), [content]);

    useEffect(() => {
        if (!isLast) {
            setDisplayedContent(content);
            setCharIndex(segments.length);
            return;
        }

        if (charIndex < segments.length) {
            const timeout = setTimeout(() => {
                setDisplayedContent((prev) => prev + segments[charIndex]);
                setCharIndex((prev) => prev + 1);
            }, delay);
            return () => clearTimeout(timeout);
        } else {
            onFinish?.();
        }
    }, [segments, charIndex, isLast, content, delay, onFinish]);

    useEffect(() => {
        onUpdate?.();
    }, [displayedContent, onUpdate]);

    return (
        <div
            className="typewriter-container"
            style={{
                position: "relative",
                display: "block",
                wordBreak: "break-word",
            }}
        >
            <Markdown
                remarkPlugins={[remarkMath]}
                rehypePlugins={[rehypeKatex]}
                className={className}
                components={{
                    p: ({ children }) => <p style={{ margin: 0 }}>{children}</p>,
                    li: ({ children }) => (
                        <div style={{ display: "flex", alignItems: "flex-start", margin: "4px 0" }}>
                            <span style={{ marginRight: "10px", flexShrink: 0, fontWeight: "bold", color: "#000" }}>•</span>
                            <div style={{ flex: 1 }}>{children}</div>
                        </div>
                    ),
                    code: (props: any) => {
                        const { children } = props;
                        if (children && String(children).includes(":::cursor:::")) {
                            return (
                                <motion.span
                                    animate={{ scale: [1, 1.2, 1] }}
                                    transition={{ duration: 0.6, repeat: Infinity }}
                                    style={{
                                        fontSize: "18px",
                                        marginLeft: "2px",
                                        display: "inline-block",
                                        verticalAlign: "middle",
                                    }}
                                >
                                    🖋️
                                </motion.span>
                            );
                        }
                        return <code>{children}</code>;
                    },
                }}
            >
                {displayedContent.replace(/\n+$/, "") + (isLast && charIndex < segments.length ? " `:::cursor:::`" : "")}
            </Markdown>
        </div>
    );
};

export default TypewriterMarkdown;
